import { useTypedSelector } from "@lib/redux";
import {
  AuthRoles,
  TransactionProxyState,
  TransactionProxyStatus,
  TransactionType,
  TransactionTypeLog,
  UseMutationProps,
  UseQueryProps,
} from "@lib/types";
import { Alert, Row, Col, Divider } from "antd";
import { useIntl } from "react-intl";
import { AppCard, AppTabs } from "@lib/components/AppComponents";
import {
  WorkflowSteps,
  TransactionDetails,
  CreateForm,
  ConfigurationForm,
  PaymentInstructions,
  PaymentConfirmation,
  ApprovalActions,
  AdminEditForm,
  TransactionLogs,
} from "./components";
import { memo, useMemo } from "react";
import _ from "lodash";
import FileUpload from "@lib/components/PageComponents/PaymentHistory/Drawer/components/FileUpload";
import { ActionButton } from "@lib/components/MiniComponents/Buttons";
import { useApi } from "@lib/hooks/APIHooks";

type Props = {
  transaction?: TransactionType;
  isCreateMode: boolean;
  paymentHistoryTransactionsQuery?: UseQueryProps<TransactionType>;
  transactionCreateMutation: UseMutationProps<Partial<TransactionType>>;
  transactionUpdateMutation: UseMutationProps<Partial<TransactionType>>;
  onTransactionSelect?: (data: TransactionType) => void;
  createTransactionLog: (
    data: Pick<TransactionTypeLog, "transactionId" | "comment" | "type">,
  ) => void;
};

function TransactionWorkflow(props: Props) {
  const {
    transaction,
    isCreateMode,
    paymentHistoryTransactionsQuery,
    transactionCreateMutation,
    transactionUpdateMutation,
    createTransactionLog,
  } = props;
  const { messages } = useIntl();
  const { user } = useTypedSelector((state) => state.auth);
  const role = user?.role;
  const { refetchCaches } = useApi();

  // Role-based permissions - memoized to prevent unnecessary re-renders
  const permissions = useMemo(
    () => ({
      canCreate: true, // All roles can create
      canEditConfig:
        role === AuthRoles.ProviderAgent || role === AuthRoles.Admin,
      canEditPayment:
        role === AuthRoles.CustomerAgent || role === AuthRoles.Admin,
      canApprove: role === AuthRoles.ProviderAgent || role === AuthRoles.Admin,
      canEditAll: role === AuthRoles.Admin,
    }),
    [role],
  );

  return (
    <div style={{ padding: "0" }}>
      {/* Workflow Steps */}
      <AppCard
        style={{ marginBottom: 24, padding: 10 }}
        borderFull
        coloredHeader
      >
        <WorkflowSteps transaction={transaction} isCreateMode={isCreateMode} />
      </AppCard>

      {/* Transaction Details */}
      {transaction && (
        <AppCard
          title={messages["common.transactionDetails"] as string}
          style={{ marginBottom: 24 }}
          borderFull
          coloredHeader
          collapse={true}
        >
          <TransactionDetails transaction={transaction} />
        </AppCard>
      )}

      {/* Action Forms */}
      <Row gutter={[16, 16]}>
        {/* Create Form */}
        {isCreateMode && (
          <Col span={24}>
            <AppCard
              title={messages["common.createPaymentRequest"] as string}
              borderFull
              coloredHeader
              collapse={true}
            >
              <CreateForm mutationQuery={transactionCreateMutation} />
            </AppCard>
          </Col>
        )}

        {/* Configuration Form */}
        {!isCreateMode &&
          permissions.canEditConfig &&
          transaction?.state ===
            TransactionProxyState.PendingForAccountInfo && (
            <Col span={24}>
              <AppCard
                title={messages["common.configurePayment"] as string}
                borderFull
                coloredHeader
                style={{ padding: 10 }}
                collapse={true}
              >
                <Alert
                  message={
                    messages["common.configurePaymentDescription"] as string
                  }
                  type="info"
                  style={{ marginBottom: 16 }}
                />
                <ConfigurationForm
                  transaction={transaction}
                  transactionUpdateMutation={transactionUpdateMutation}
                  role={role as AuthRoles}
                />
              </AppCard>
            </Col>
          )}

        {/* Payment Instructions */}
        {!isCreateMode &&
          transaction?.providerTransactionConfigId &&
          transaction?.state ===
            TransactionProxyState.PendingCustomerApproval && (
            <Col span={24}>
              <AppCard
                title={messages["common.paymentInstructions"] as string}
                borderFull
                coloredHeader
                collapse={true}
              >
                <Alert
                  message={
                    messages["common.paymentInstructionsDescription"] as string
                  }
                  type="warning"
                  // style={{ marginBottom: 16 }}
                />
                <PaymentInstructions
                  transaction={transaction}
                  createTransactionLog={createTransactionLog}
                />
              </AppCard>
            </Col>
          )}

        {!!transaction?.id && (
          <Col span={24}>
            <AppCard
              title={messages["common.paymentDocuments"] as string}
              borderFull
              coloredHeader
              collapse={true}
              extra={
                <ActionButton
                  type="refresh"
                  noLabel
                  onClick={() => {
                    refetchCaches({ searchKeys: ["attachments"] });
                  }}
                />
              }
            >
              <FileUpload
                transaction={transaction}
                transactionUpdateMutation={transactionUpdateMutation}
                paymentHistoryTransactionsQuery={
                  paymentHistoryTransactionsQuery
                }
                fileUploadProps={{
                  canDelete: permissions.canEditAll,
                  canUpload:
                    transaction?.status === TransactionProxyStatus.Pending ||
                    permissions.canEditAll,
                  showManager: permissions.canEditAll,
                  showManagerLink: permissions.canEditAll,
                }}
                createTransactionLog={createTransactionLog}
              />
            </AppCard>
          </Col>
        )}

        {/* Customer Payment Confirmation */}
        {!isCreateMode &&
          transaction?.providerTransactionConfigId &&
          transaction?.state ===
            TransactionProxyState.PendingCustomerApproval &&
          permissions.canEditPayment && (
            <Col span={24}>
              <AppCard
                title={messages["common.paymentConfirmation"] as string}
                borderFull
                coloredHeader
                collapse={true}
              >
                <Alert
                  message={
                    messages["common.paymentConfirmationDescription"] as string
                  }
                  type="info"
                  style={{ marginBottom: 16 }}
                />
                <PaymentConfirmation
                  transaction={transaction}
                  transactionUpdateMutation={transactionUpdateMutation}
                  paymentHistoryTransactionsQuery={
                    paymentHistoryTransactionsQuery
                  }
                />
              </AppCard>
            </Col>
          )}

        {/* Approval Actions */}
        {!isCreateMode &&
          transaction?.state ===
            TransactionProxyState.PendingProviderApproval &&
          permissions.canApprove && (
            <Col span={24}>
              <AppCard
                title={messages["common.approvePayment"] as string}
                borderFull
                coloredHeader
                collapse={true}
              >
                <Alert
                  message={
                    messages["common.approvePaymentDescription"] as string
                  }
                  type="info"
                  style={{ marginBottom: 16 }}
                />
                <ApprovalActions
                  transaction={transaction}
                  transactionUpdateMutation={transactionUpdateMutation}
                />
              </AppCard>
            </Col>
          )}

        {/* Status Messages */}
        {!isCreateMode &&
          transaction?.state === TransactionProxyState.ApprovedByProvider && (
            <Col span={24}>
              <AppCard borderFull coloredHeader>
                <Alert
                  message={messages["common.paymentCompleted"] as string}
                  type="success"
                  showIcon
                />
              </AppCard>
            </Col>
          )}

        {/* Admin Edit Form - All Fields */}
        {!isCreateMode && permissions.canEditAll && (
          <Col span={24}>
            <Divider children={messages["common.adminEdit"] as string} />

            <Alert
              message={messages["common.adminEditDescription"] as string}
              type="error"
              style={{ marginBottom: 16 }}
            />

            <AppTabs
              sticky
              tabs={[
                {
                  key: "edit",
                  Title: messages["common.edit"] as string,
                  Component: AdminEditForm,
                  props: {
                    transaction,
                    transactionUpdateMutation,
                    role: role as AuthRoles,
                  },
                },
                {
                  key: "logs",
                  Title: messages["common.logTimeline"] as string,
                  Component: TransactionLogs,
                  props: {
                    transaction,
                  },
                },
                // {
                //   key: "files",
                //   Title: messages["common.files"] as string,
                //   Component: FileUpload,
                //   props: {
                //     transaction,
                //     transactionUpdateMutation,
                //     paymentHistoryTransactionsQuery,
                //     createTransactionLog,
                //   },
                // },
              ]}
            />
          </Col>
        )}
      </Row>
    </div>
  );
}

export default memo(TransactionWorkflow);
